import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { httpService, IconWrapper } from '@flexo/general';
import { useDispatch } from 'react-redux';

import styles from './register-by-invite.module.scss';
import { ALERT } from '@flexo/reducers';
import { Button, Textfield, PasswordConditions, PasswordValidation, validatePassword, isPasswordValid, doPasswordsMatch } from '@flexo/atoms';
import { Player } from '@lottiefiles/react-lottie-player';
import queryString from 'query-string';

const ENV = (import.meta as any).env;
const loader = '/loaderLottie.json';
let _askError = false;
let ranOnce = false;

let _formdata = {
  password: '',
  password_confirm: '',
};

// let _paramsData = {
//   email: '',
//   oob_code: '',
// }

export function RegisterByInvite() {
  const formRef = useRef<any>(null);
  const passwordRef = useRef<any>(null);
  const confirmPasswordRef = useRef<any>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [passwordresetAsked, setPasswordResetAsked] = useState(false);
  const [askError, setAskError] = useState(false);
  const [paramsData, setParamsData] = useState({
    email: '',
    oob_code: '',
  });

  const { title, subtitle, inputs, buttonLabel } = t(
    'pages.register_by_invite',
    { returnObjects: true, email: paramsData.email }
  ) as any;

  const [formData, setFormData] = useState(_formdata);

  const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>({
    lengthValid: false,
    hasNumber: false,
    hasSpecialChar: false,
    hasUpperCase: false,
    hasLowerCase: false,
    passwordsMatch: false,
  });

  const [passwordBlur, setPasswordBlur] = useState(false);

  const [toggleIcons, setToggleIcons] = useState({
    password: 'hide',
    password_confirm: 'hide',
  });

  const [toggleTypes, setToggleTypes] = useState({
    password: 'password',
    password_confirm: 'password',
  });

  async function handleInputChange(event: any) {
    const { name, value } = event.target;

    _askError = false;
    setAskError(_askError);

    _formdata = { ..._formdata, [name]: value };
    setFormData(_formdata);

    if (name === 'password') {
      const validation = validatePassword(value, formData.password_confirm);
      setPasswordValidation(validation);
    }
    if (name === 'password_confirm') {
      setPasswordValidation((prev) => ({
        ...prev,
        passwordsMatch: formData.password === value,
      }));
    }
  }

  function togglePasswordVisibility(inputName: string) {
    setToggleIcons({
      ...toggleIcons,
      [inputName]: toggleIcons[inputName] === 'hide' ? 'show' : 'hide',
    });

    setToggleTypes({
      ...toggleTypes,
      [inputName]: toggleTypes[inputName] === 'password' ? 'text' : 'password',
    });
  }

  const handleBlur = (event: any) => {
    const { name } = event.target;
    if (name === 'password') {
      setPasswordBlur(true);
    }
  };

  const handleFocus = (event: any) => {
    const { name } = event.target;
    if (name === 'password') {
      setPasswordBlur(false);
    }
  };

  async function askForRegistration() {
    setPasswordResetAsked(true);

    const response = await httpService
      .post({
        url: `${ENV.VITE_APP_BASE_URL}/auth/v1_1/register`,
        data: {
          email: paramsData.email,
          activation_code: paramsData.oob_code,
          password: formData.password,
        },
      })
      .then((resp: any) => ({
        status: resp.status,
        statusText: resp?.statusText || 'Unknown status text',
        message: resp?.data?.message || 'Unknown message',
      }))
      .catch((error) => {
        // console.warn(error);
        return {
          status: error?.response?.status || 500,
          statusText: error?.response?.statusText || 'Unknown error',
        };
      });

    setPasswordResetAsked(false);

    if (response.status > 200) {
      if (response.status === 422) {
        dispatch(
          ALERT.SetAlert({
            payload: {
              value: {
                type: 'BANNER_UNEXPECTED_ERROR',
              },
            },
          } as any)
        );
      } else if (response.status === 403) {
        dispatch(
          ALERT.SetAlert({
            payload: {
              value: {
                type: 'BANNER_NOT_ALLOWED',
              },
            },
          } as any)
        );
      } else {
        dispatch(
          ALERT.SetAlert({
            payload: {
              value: {
                type: 'BANNER_UNEXPECTED_ERROR',
              },
            },
          } as any)
        );
      }

      _askError = true;
      setAskError(_askError);
    } else {
      _askError = false;
      setAskError(_askError);

      dispatch(
        ALERT.SetAlert({
          payload: {
            value: {
              type: 'BANNER_REGISTRATION_SUCCESS',
            },
          },
        } as any)
      );

      setTimeout(() => {
        dispatch(ALERT.ResetAlert({} as any));

        navigate('/login');
      }, 4000);
    }
  }

  useEffect(() => {
    if (ranOnce) return;

    const params: any = queryString.parse(window.location.search);


    const keywords = ['email', 'oob_code'];

    // eslint-disable-next-line
    if (keywords.every((key: string) => Object.prototype.hasOwnProperty.call(params, key))) {
      setParamsData({
        email: params.email,
        oob_code: params.oob_code,
      });
    } else {
      dispatch(ALERT.SetAlert({
        payload: {
          value: {
            type: 'BANNER_WRONG_PARAMS',
          },
        },
      } as any));

      setTimeout(() => {
        dispatch(ALERT.ResetAlert({} as any));

        navigate('/login');
      }, 4000);
    }

    ranOnce = true;
  }, []);

  return (
    <div className={styles.RegisterByInvite}>
      <div className={styles.RegisterByInvite__Header}>
        <IconWrapper
          iconName={'logo_wide'}
          className={styles.RegisterByInvite__Header__Logo}
        />
        <div className={styles.RegisterByInvite__Header__Title}>{title}</div>
        <div className={styles.RegisterByInvite__Header__Email}>{subtitle}</div>
      </div>

      <form
        id="registration_web_form"
        className={styles.RegisterByInvite__Form}
        ref={formRef}
      >
        {/* password */}

        <div className={styles.RegisterByInvite__Form__Input}>
          <Textfield
            variant="default"
            ref={passwordRef}
            label={inputs.password.label}
            name={inputs.password.name}
            type={toggleTypes[inputs.password.name]}
            placeholder={inputs.password.placeholder}
            onPaste={(e) => false}
            onCopy={(e) => false}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck="false"
            autoComplete="off"
            defaultValue={formData[inputs.password.name]}
            onChange={(ev: any) => handleInputChange(ev)}
            onBlur={handleBlur}
            onFocus={handleFocus}
            icon={toggleIcons[inputs.password.name]}
            onIconClick={() => togglePasswordVisibility(inputs.password.name)}
          />
        </div>

        {/* password conditions */}

        <PasswordConditions
          validation={passwordValidation}
          isFocused={!passwordBlur}
          conditionClassName={styles.RegisterByInvite__Conditions}
        />

        {/* password_confirm */}

        <div className={styles.RegisterByInvite__Form__Input}>
          <Textfield
            variant="default"
            ref={confirmPasswordRef}
            label={inputs.password_confirm.label}
            name={inputs.password_confirm.name}
            type={toggleTypes[inputs.password_confirm.name]}
            placeholder={inputs.password_confirm.placeholder}
            onPaste={(e) => false}
            onCopy={(e) => false}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck="false"
            defaultValue={formData[inputs.password_confirm.name]}
            onChange={handleInputChange}
            autoComplete="off"
            icon={toggleIcons[inputs.password_confirm.name]}
            onIconClick={() =>
              togglePasswordVisibility(inputs.password_confirm.name)
            }
          />
        </div>
      </form>

      <div className={styles.RegisterByInvite__Footer}>
        <Button
          className={styles.RegisterByInvite__Footer__Button}
          label={
            passwordresetAsked ? (
              <Player autoplay loop src={loader} style={{ height: 20 }} />
            ) : (
              buttonLabel
            )
          }
          variant="primary"
          color="secondary"
          disabled={
            !isPasswordValid(formData.password) ||
            !doPasswordsMatch(formData.password, formData.password_confirm)
          }
          onClick={() => askForRegistration()}
        />
      </div>
    </div>
  );
}
